import { useEffect, useRef, useState, type RefObject } from "react";
import { ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  archivosDeCarpetas,
  archivosDesdeUrls,
  archivosPegados,
  archivosSueltos,
  carpetasArrastradas,
  arrastreTraeArchivos,
  esCampoEditable,
  leerArchivosPortapapeles,
  pegadoTraeTexto,
  urlsDeImagenArrastradas,
} from "@/lib/pegadoArchivos";

/**
 * Estándar de las zonas de carga del admin: además de clic, aceptan ARRASTRAR y PEGAR.
 *
 *  - Pegar (Ctrl/Cmd+V, Win+V) sobre la zona o en CUALQUIER parte del modal que la contiene
 *    (`[role="dialog"]`: Dialog, Sheet, ModalForm): al abrir un modal el foco suele estar en un
 *    campo de texto, no en la zona. Si el foco está en un campo y el portapapeles también trae
 *    texto, el texto se pega normal y solo se adjuntan los archivos.
 *  - Arrastrar sobre la zona o sobre cualquier parte del modal. Si lo arrastrado es una imagen
 *    de otra página que solo trae su dirección, se intenta descargarla (si el sitio lo permite).
 *  - Quien ya manejó el evento (un textarea que adjunta capturas, otra zona) llama
 *    `preventDefault` y aquí se ignora: nada se adjunta dos veces.
 *  - Con varias zonas en el mismo modal, lo pegado va a la zona enfocada o a la última que se
 *    tocó; si ninguna, no se adjunta (no hay forma de saber a cuál iba). Igual el arrastre: con
 *    varias zonas, solo cuenta el que se suelta sobre la zona.
 *
 *   const zona = useRef<HTMLDivElement>(null);
 *   const { arrastrando, zonaProps } = usePegadoArchivos(zona, (files) => agregar(files), { disabled });
 *   <div ref={zona} {...zonaProps}>…<BotonPegarArchivos onFiles={agregar} /></div>
 */

export type OrigenArchivos = "pegar" | "soltar";

export interface OpcionesPegadoArchivos {
  disabled?: boolean;
  /**
   * Escuchar pegado y arrastre en todo el modal que contiene la zona (por defecto sí). Apagarlo
   * cuando lo pegado se sube al instante y el modal tiene otros campos que también reciben
   * archivos (p. ej. el detalle de un ticket).
   */
  enModal?: boolean;
}

const ATRIBUTO_ZONA = "data-zona-archivos";
// Última zona que el usuario tocó o enfocó: decide a dónde va lo pegado si un modal tiene varias.
let ultimaZona: HTMLElement | null = null;

const AVISO_SIN_IMAGEN =
  "No se pudo traer esa imagen desde la otra página. Cópiala (clic derecho, Copiar imagen) y pégala aquí, o guárdala y súbela.";

function zonasEn(scope: Element) {
  return scope.querySelectorAll(`[${ATRIBUTO_ZONA}]`).length;
}

/** Entrega los archivos del evento; si solo trae URLs de imagen, intenta descargarlas. */
function recibirDeTransferencia(
  dt: DataTransfer | null,
  entregar: (files: File[]) => void,
  origen: OrigenArchivos,
): boolean {
  // Una carpeta soltada entrega lo que trae adentro (se lee asíncrono, ya fuera del evento).
  const carpetas = origen === "soltar" ? carpetasArrastradas(dt) : [];
  if (carpetas.length) {
    const sueltos = archivosSueltos(dt, carpetas);
    void archivosDeCarpetas(carpetas)
      .then((deCarpetas) => {
        const todos = [...sueltos, ...deCarpetas];
        if (todos.length) entregar(todos);
        else toast.info("La carpeta que soltaste está vacía.");
      })
      .catch(() => toast.error("No se pudo leer la carpeta. Elige los archivos o usa Seleccionar carpeta."));
    return true;
  }
  const files = archivosPegados(dt);
  if (files.length) {
    entregar(files);
    return true;
  }
  if (origen !== "soltar") return false;
  const urls = urlsDeImagenArrastradas(dt); // se lee ya: después del evento getData no responde
  if (!urls.length) return false;
  void archivosDesdeUrls(urls).then((desc) => (desc.length ? entregar(desc) : toast.error(AVISO_SIN_IMAGEN)));
  return true;
}

export function usePegadoArchivos<T extends HTMLElement>(
  rootRef: RefObject<T>,
  onFiles: (files: File[], origen: OrigenArchivos) => void,
  { disabled = false, enModal = true }: OpcionesPegadoArchivos = {},
) {
  const [arrastrando, setArrastrando] = useState(false);
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // El dragover se repite mientras se arrastra: si deja de llegar, ya se fue (más fiable que
  // dragleave, que también salta al pasar sobre los hijos).
  const marcarArrastre = () => {
    setArrastrando(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setArrastrando(false), 150);
  };
  const terminarArrastre = () => {
    clearTimeout(timer.current);
    setArrastrando(false);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  // Marca esta zona como la última usada (desempata entre varias zonas del mismo modal).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const marcar = () => (ultimaZona = root);
    root.addEventListener("pointerdown", marcar);
    root.addEventListener("focusin", marcar);
    // No se limpia `ultimaZona` al desmontar: el efecto corre en cada render y la borraría. Una
    // zona ya desmontada no estorba porque `esMio` exige que siga en el documento.
    return () => {
      root.removeEventListener("pointerdown", marcar);
      root.removeEventListener("focusin", marcar);
    };
  });

  // Pegado y arrastre en todo el modal que contiene la zona.
  useEffect(() => {
    if (disabled || !enModal) return;

    // El evento es de esta zona si cae en el modal y, con varias zonas, si es la indicada.
    const esMio = (target: Node | null, estricto: boolean) => {
      const root = rootRef.current;
      const scope = root?.closest('[role="dialog"]');
      if (!root || !scope || !target) return false;
      if (root.contains(target)) return true;
      if (!(scope.contains(target) || target === document.body)) return false;
      if (zonasEn(scope) <= 1) return true;
      return !estricto && ultimaZona === root && root.isConnected;
    };

    const onPaste = (e: ClipboardEvent) => {
      if (e.defaultPrevented || disabledRef.current) return;
      const target = e.target as Node | null;
      if (!esMio(target, false)) return;
      const files = archivosPegados(e.clipboardData);
      if (!files.length) return;
      if (!(esCampoEditable(target) && pegadoTraeTexto(e.clipboardData))) e.preventDefault();
      onFilesRef.current(files, "pegar");
    };

    const aceptaArrastre = (e: DragEvent) => {
      if (e.defaultPrevented || disabledRef.current || !arrastreTraeArchivos(e.dataTransfer)) return false;
      // Un enlace o texto arrastrado a un campo de texto se deja caer como texto.
      const traeArchivos = Array.from(e.dataTransfer?.types ?? []).includes("Files");
      if (!traeArchivos && esCampoEditable(e.target)) return false;
      return esMio(e.target as Node | null, true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!aceptaArrastre(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      marcarArrastre();
    };
    const onDrop = (e: DragEvent) => {
      if (!aceptaArrastre(e)) return;
      e.preventDefault();
      terminarArrastre();
      const ok = recibirDeTransferencia(e.dataTransfer, (f) => onFilesRef.current(f, "soltar"), "soltar");
      if (!ok) toast.info("Lo que soltaste no trae un archivo. Arrastra el archivo o una imagen.");
    };

    document.addEventListener("paste", onPaste);
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("drop", onDrop);
    return () => {
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("drop", onDrop);
    };
    // rootRef es estable; lo demás se lee por ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, enModal]);

  // Manejadores de la zona misma (funcionan también fuera de un modal). Llaman preventDefault,
  // así el escucha del modal no vuelve a entregar lo mismo.
  const zonaProps = {
    [ATRIBUTO_ZONA]: disabled ? undefined : "",
    onPaste: (e: React.ClipboardEvent) => {
      if (disabledRef.current || e.defaultPrevented) return;
      const files = archivosPegados(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      onFilesRef.current(files, "pegar");
    },
    onDragOver: (e: React.DragEvent) => {
      if (disabledRef.current || !arrastreTraeArchivos(e.dataTransfer)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      marcarArrastre();
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) terminarArrastre();
    },
    onDrop: (e: React.DragEvent) => {
      if (disabledRef.current) return;
      e.preventDefault();
      terminarArrastre();
      const ok = recibirDeTransferencia(e.dataTransfer, (f) => onFilesRef.current(f, "soltar"), "soltar");
      if (!ok) toast.info("Lo que soltaste no trae un archivo. Arrastra el archivo o una imagen.");
    },
  };

  return { arrastrando, zonaProps };
}

/** Lee el portapapeles (Clipboard API) y avisa si no se puede o si no trae archivos. */
export async function pegarDelPortapapeles(onFiles: (files: File[]) => void) {
  const files = await leerArchivosPortapapeles();
  if (files === null) {
    toast.error("Tu navegador no permite leer el portapapeles desde aquí. Haz clic en la zona y pega con Ctrl/Cmd+V.");
    return;
  }
  if (!files.length) {
    toast.info("El portapapeles no tiene imágenes ni archivos. Copia una captura o un archivo y vuelve a intentar.");
    return;
  }
  onFiles(files);
}

/**
 * Botón "Pegar del portapapeles": la única vía en el teléfono (no hay Ctrl+V). No propaga el
 * clic, así que puede ir dentro de una zona clicable sin abrir el selector de archivos.
 */
export function BotonPegarArchivos({
  onFiles,
  disabled,
  className,
  size = "sm",
  variant = "outline",
  children = "Pegar del portapapeles",
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  children?: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={cn("h-8 bg-background", className)}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        void pegarDelPortapapeles(onFiles);
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <ClipboardPaste className="size-3.5" /> {children}
    </Button>
  );
}
